// Campaign skeleton (GDD §5 / M3 — stretch goal). An 8-province mini-continent: pick a
// faction, take one action per turn (attack an adjacent enemy/neutral province → launches
// the battle scene; or fortify), then the rival AI takes its turn (auto-resolved with a
// headlines ticker). Whole campaign is one JSON document saved to localStorage. Win by
// holding all provinces.

import type { Faction } from "../sim/types";
import { FACTIONS } from "../sim/data";
import { ScreenBase } from "./screen";
import { background, wrapText } from "./menus";
import { roundRectPath } from "../render/art";

export interface Province {
  id: number;
  name: string;
  x: number; // normalized 0..1
  y: number;
  owner: Faction | "neutral";
  income: number;
  fort: number;
  adj: number[];
  isCapital?: Faction;
}

export interface CampaignState {
  version: 1;
  turn: number;
  playerFaction: Faction;
  provinces: Province[];
  headlines: string[];
  over: Faction | null;
  awaitingBattle: number | null; // province id the player is attacking
}

const SAVE_KEY = "ashfall_campaign_v1";

export function createCampaign(playerFaction: Faction): CampaignState {
  const enemy = other(playerFaction);
  const P = (id: number, name: string, x: number, y: number, income: number, adj: number[], owner: Province["owner"] = "neutral", isCapital?: Faction): Province => ({ id, name, x, y, income, fort: 0, adj, owner, isCapital });
  const provinces: Province[] = [
    P(0, "Ashmoor", 0.12, 0.32, 4, [2], playerFaction, playerFaction),
    P(1, "Ironvale", 0.14, 0.72, 3, [2, 5], playerFaction),
    P(2, "Greywatch", 0.34, 0.5, 3, [0, 1, 3, 4]),
    P(3, "Cinderhold", 0.5, 0.24, 4, [2, 4, 6]),
    P(4, "Vael City", 0.52, 0.55, 8, [2, 3, 5, 6, 7]),
    P(5, "Emberfall", 0.5, 0.82, 3, [1, 4, 7]),
    P(6, "Duskreach", 0.72, 0.34, 3, [3, 4, 7], enemy),
    P(7, "Hollowmere", 0.86, 0.64, 4, [4, 5, 6], enemy, enemy),
  ];
  return { version: 1, turn: 1, playerFaction, provinces, headlines: [`The war for Vael begins. ${FACTIONS[playerFaction].name} muster their host.`], over: null, awaitingBattle: null };
}

export function other(f: Faction): Faction {
  return f === "legion" ? "kindled" : "legion";
}

export function saveCampaign(s: CampaignState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable */
  }
}

export function loadCampaign(): CampaignState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as CampaignState;
    if (s.version !== 1) return null;
    return s;
  } catch {
    return null;
  }
}

export function hasSave(): boolean {
  return !!loadCampaign();
}

function strength(prov: Province, attacker: Faction): number {
  return prov.owner === attacker ? prov.income + prov.fort * 3 : prov.income + prov.fort * 3 + (prov.owner === "neutral" ? 0 : 4);
}

/** Apply the result of a player-fought attack battle. */
export function applyPlayerBattle(s: CampaignState, provinceId: number, won: boolean): void {
  const prov = s.provinces.find((p) => p.id === provinceId);
  if (!prov) return;
  if (won) {
    const was = prov.owner;
    prov.owner = s.playerFaction;
    prov.fort = Math.max(0, prov.fort - 1);
    s.headlines.unshift(`${FACTIONS[s.playerFaction].name} seize ${prov.name}${was !== "neutral" ? ` from the ${FACTIONS[was].name}` : ""}.`);
  } else {
    s.headlines.unshift(`${FACTIONS[s.playerFaction].name} are repulsed at ${prov.name}.`);
  }
  s.awaitingBattle = null;
  endPlayerTurn(s);
}

export function fortify(s: CampaignState, provinceId: number): boolean {
  const prov = s.provinces.find((p) => p.id === provinceId);
  if (!prov || prov.owner !== s.playerFaction) return false;
  prov.fort = Math.min(3, prov.fort + 1);
  s.headlines.unshift(`${prov.name} is fortified (level ${prov.fort}).`);
  endPlayerTurn(s);
  return true;
}

/** The rival AI takes one action, auto-resolved. */
function endPlayerTurn(s: CampaignState): void {
  if (checkOver(s)) return;
  aiTurn(s);
  s.turn++;
  checkOver(s);
  trimHeadlines(s);
  saveCampaign(s);
}

function aiTurn(s: CampaignState): void {
  const enemy = other(s.playerFaction);
  // find best (weakest adjacent, non-enemy) province to attack from an owned one
  let bestFrom: Province | null = null;
  let bestTarget: Province | null = null;
  let bestScore = -Infinity;
  for (const from of s.provinces) {
    if (from.owner !== enemy) continue;
    for (const nId of from.adj) {
      const t = s.provinces.find((p) => p.id === nId)!;
      if (t.owner === enemy) continue;
      const score = t.income + (t.owner === s.playerFaction ? 1 : 3) - strength(t, enemy) * 0.5;
      if (score > bestScore) {
        bestScore = score;
        bestFrom = from;
        bestTarget = t;
      }
    }
  }
  if (!bestTarget || !bestFrom) {
    s.headlines.unshift(`${FACTIONS[enemy].name} regroup.`);
    return;
  }
  const atk = strength(bestFrom, enemy) + 2 + Math.random() * 6;
  const def = strength(bestTarget, s.playerFaction === bestTarget.owner ? s.playerFaction : enemy) + Math.random() * 5;
  if (atk > def) {
    const was = bestTarget.owner;
    bestTarget.owner = enemy;
    bestTarget.fort = Math.max(0, bestTarget.fort - 1);
    s.headlines.unshift(`${FACTIONS[enemy].name} storm ${bestTarget.name}${was === s.playerFaction ? " — a province is lost!" : "."}`);
  } else {
    s.headlines.unshift(`${FACTIONS[enemy].name}'s assault on ${bestTarget.name} is broken.`);
  }
}

function checkOver(s: CampaignState): boolean {
  const enemy = other(s.playerFaction);
  const mine = s.provinces.filter((p) => p.owner === s.playerFaction).length;
  const theirs = s.provinces.filter((p) => p.owner === enemy).length;
  if (theirs === 0 && mine > 0) {
    s.over = s.playerFaction;
    s.headlines.unshift(`VAEL IS UNITED under ${FACTIONS[s.playerFaction].name}!`);
    return true;
  }
  if (mine === 0) {
    s.over = enemy;
    s.headlines.unshift(`${FACTIONS[s.playerFaction].name} are driven from Vael.`);
    return true;
  }
  return false;
}

function trimHeadlines(s: CampaignState): void {
  if (s.headlines.length > 8) s.headlines.length = 8;
}

export function income(s: CampaignState, f: Faction): number {
  return s.provinces.filter((p) => p.owner === f).reduce((a, p) => a + p.income, 0);
}

// ---- scene -----------------------------------------------------------------

export interface CampaignCallbacks {
  onAttackBattle: (provinceId: number) => void; // launch the battle scene
  onBack: () => void;
}

export class CampaignScene extends ScreenBase {
  private state: CampaignState;
  private cb: CampaignCallbacks;
  private selected: number | null = null;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, state: CampaignState, cb: CampaignCallbacks) {
    super(canvas, ctx);
    this.state = state;
    this.cb = cb;
  }

  private px(nx: number): number {
    return 60 + nx * (this.vw - 380);
  }
  private py(ny: number): number {
    return 120 + ny * (this.vh - 260);
  }

  draw(time: number): void {
    const ctx = this.ctx;
    this.clearCursor();
    background(ctx, this.vw, this.vh, time);
    const s = this.state;

    // title / turn / income
    ctx.fillStyle = "#e8e0d4";
    ctx.font = "800 30px Trebuchet MS";
    ctx.fillText("THE CRUSADE FOR VAEL", 40, 56);
    ctx.font = "600 15px Trebuchet MS";
    ctx.fillStyle = "#b0a894";
    ctx.fillText(`Turn ${s.turn}  ·  You: ${FACTIONS[s.playerFaction].name}  (income ${income(s, s.playerFaction)})  vs  ${FACTIONS[other(s.playerFaction)].name} (income ${income(s, other(s.playerFaction))})`, 40, 80);

    // edges
    ctx.strokeStyle = "rgba(120,110,96,0.35)";
    ctx.lineWidth = 2;
    for (const p of s.provinces) for (const nId of p.adj) if (nId > p.id) {
      const q = s.provinces[nId];
      ctx.beginPath();
      ctx.moveTo(this.px(p.x), this.py(p.y));
      ctx.lineTo(this.px(q.x), this.py(q.y));
      ctx.stroke();
    }

    // provinces
    const sel = this.selected != null ? s.provinces[this.selected] : null;
    const attackable = new Set<number>();
    if (sel && sel.owner === s.playerFaction) for (const n of sel.adj) if (s.provinces[n].owner !== s.playerFaction) attackable.add(n);

    for (const p of s.provinces) {
      const x = this.px(p.x);
      const y = this.py(p.y);
      const col = p.owner === "neutral" ? "#7a746a" : FACTIONS[p.owner].colors.primary;
      const r = p.id === 4 ? 34 : 26;
      // selectable highlight
      if (this.selected === p.id) {
        ctx.strokeStyle = "#e8e0d4";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, r + 7, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (attackable.has(p.id)) {
        ctx.strokeStyle = "#ff6a4a";
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.arc(x, y, r + 7, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.5)";
      ctx.lineWidth = 2;
      ctx.stroke();
      if (p.isCapital) {
        ctx.fillStyle = "#ffe089";
        ctx.font = "700 14px Trebuchet MS";
        ctx.textAlign = "center";
        ctx.fillText("★", x, y + 5);
        ctx.textAlign = "left";
      }
      // fort pips
      for (let i = 0; i < p.fort; i++) {
        ctx.fillStyle = "#cfd8e0";
        ctx.fillRect(x - 9 + i * 8, y + r + 4, 6, 4);
      }
      // label
      ctx.fillStyle = "#e8e0d4";
      ctx.font = "600 13px Trebuchet MS";
      ctx.textAlign = "center";
      ctx.fillText(`${p.name} (${p.income})`, x, y - r - 8);
      ctx.textAlign = "left";

      this.regions.push({ x: x - r, y: y - r, w: r * 2, h: r * 2, fn: () => this.clickProvince(p.id) });
    }

    // right panel: actions
    const panelX = this.vw - 300;
    ctx.fillStyle = "rgba(18,16,13,0.9)";
    roundRectPath(ctx, panelX, 110, 280, 360, 8);
    ctx.fill();
    ctx.strokeStyle = "rgba(90,84,72,0.6)";
    ctx.stroke();
    ctx.fillStyle = "#cfc7ba";
    ctx.font = "700 14px Trebuchet MS";
    ctx.fillText("ORDERS", panelX + 16, 136);
    let py = 156;
    if (s.over) {
      ctx.fillStyle = s.over === s.playerFaction ? "#8fd07a" : "#d0483c";
      ctx.font = "800 20px Trebuchet MS";
      ctx.fillText(s.over === s.playerFaction ? "CAMPAIGN WON" : "CAMPAIGN LOST", panelX + 16, py + 6);
      py += 40;
      this.button(panelX + 16, py, 248, 40, "New Campaign", () => this.newCampaign());
      py += 48;
    } else if (sel) {
      ctx.fillStyle = "#e8e0d4";
      ctx.font = "700 15px Trebuchet MS";
      ctx.fillText(`${sel.name}${sel.isCapital ? " ★" : ""}`, panelX + 16, py);
      ctx.fillStyle = "#b0a894";
      ctx.font = "500 12px Trebuchet MS";
      ctx.fillText(`Owner: ${sel.owner === "neutral" ? "Neutral" : FACTIONS[sel.owner].name}   Income ${sel.income}   Fort ${sel.fort}`, panelX + 16, py + 18);
      py += 40;
      if (sel.owner === s.playerFaction) {
        this.button(panelX + 16, py, 248, 36, "Fortify (ends turn)", () => this.doFortify(sel.id), { color: "#3a4a2a" });
        py += 44;
        ctx.fillStyle = "#8a8272";
        ctx.font = "500 12px Trebuchet MS";
        wrapText(ctx, "Click an adjacent red-ringed province to attack — that launches a live battle.", panelX + 16, py, 248, 16);
        py += 48;
      } else if (attackable.size === 0) {
        // check if adjacent to a player province → attack from here handled by selecting player province
        ctx.fillStyle = "#8a8272";
        ctx.font = "500 12px Trebuchet MS";
        wrapText(ctx, "Select one of YOUR provinces, then click an adjacent enemy/neutral province to attack.", panelX + 16, py, 248, 16);
        py += 40;
      }
    } else {
      ctx.fillStyle = "#8a8272";
      ctx.font = "500 12px Trebuchet MS";
      wrapText(ctx, "Select one of your provinces to see orders. One action per turn: attack an adjacent province (fights a live battle) or fortify.", panelX + 16, py, 248, 16);
      py += 60;
    }

    // save / load / menu
    const by = 470;
    this.button(panelX + 16, by, 120, 34, "Save", () => saveCampaign(s));
    this.button(panelX + 144, by, 120, 34, "Reload Save", () => this.reload());
    this.button(panelX + 16, by + 42, 248, 34, "Main Menu", () => this.cb.onBack());

    // headlines ticker
    ctx.fillStyle = "rgba(18,16,13,0.9)";
    roundRectPath(ctx, 40, this.vh - 110, this.vw - 360, 84, 8);
    ctx.fill();
    ctx.strokeStyle = "rgba(90,84,72,0.6)";
    ctx.stroke();
    ctx.fillStyle = "#e0771b";
    ctx.font = "700 12px Trebuchet MS";
    ctx.fillText("DISPATCHES", 54, this.vh - 90);
    ctx.font = "500 13px Trebuchet MS";
    s.headlines.slice(0, 3).forEach((h, i) => {
      ctx.fillStyle = i === 0 ? "#e8e0d4" : "#9a9284";
      ctx.fillText(`•  ${h}`, 54, this.vh - 70 + i * 18);
    });
  }

  private clickProvince(id: number): void {
    const s = this.state;
    if (s.over) return;
    const p = s.provinces[id];
    if (this.selected != null) {
      const sel = s.provinces[this.selected];
      if (sel.owner === s.playerFaction && sel.adj.includes(id) && p.owner !== s.playerFaction) {
        // attack → launch battle
        s.awaitingBattle = id;
        saveCampaign(s);
        this.cb.onAttackBattle(id);
        return;
      }
    }
    this.selected = id;
  }

  private doFortify(id: number): void {
    fortify(this.state, id);
    this.selected = null;
  }

  private newCampaign(): void {
    const fresh = createCampaign(this.state.playerFaction);
    Object.assign(this.state, fresh);
    saveCampaign(this.state);
    this.selected = null;
  }

  private reload(): void {
    const loaded = loadCampaign();
    if (loaded) {
      Object.assign(this.state, loaded);
      this.selected = null;
    }
  }
}
